import { FilterChipSelectItemLabelSlot, FilterChipSelectItemSlot } from '@magicschool/spellbook/components/FilterChipSelect/slots';
import { isValidElement } from 'react';

type FilterChipSelectItemMap = Map<string, { value: string; label?: string }>;

/** Recursively extracts values from FilterChipSelectItemSlot children so we can use them in the context and access them in any FilterChipSelect component.
 *
 * @TODO [SPE-66] Apply contextual composition to all Selects
 * https://linear.app/magicschool/issue/SPE-66/apply-contextual-composition-to-all-selects
 */
export const extractValuesFromFilterChipSelectItemSlots = (children: React.ReactNode): FilterChipSelectItemMap => {
  const itemsData: FilterChipSelectItemMap = new Map();
  if (!children) return itemsData;
  const processNode = (node: React.ReactNode): void => {
    if (Array.isArray(node)) {
      node.forEach(processNode);
      return;
    }

    if (!isValidElement(node)) {
      return;
    }

    if (node.type === FilterChipSelectItemSlot) {
      const { value, label, children: childContent } = node.props as React.ComponentProps<typeof FilterChipSelectItemSlot>;

      if (typeof value !== 'string') {
        return;
      }

      let derivedLabel: string | undefined = typeof label === 'string' ? label : undefined;

      if (!derivedLabel) {
        const slotChildren =
          isValidElement(node) && node.props && typeof node.props === 'object'
            ? (node.props as { children?: React.ReactNode }).children
            : undefined;

        if (Array.isArray(slotChildren)) {
          for (const arrayChild of slotChildren) {
            if (
              isValidElement<React.ComponentProps<typeof FilterChipSelectItemLabelSlot>>(arrayChild) &&
              arrayChild.type === FilterChipSelectItemLabelSlot
            ) {
              const itemLabelProps = arrayChild.props;
              if (typeof itemLabelProps.children === 'string') {
                derivedLabel = itemLabelProps.children;
                break;
              }
            }
          }
        }

        if (!derivedLabel && isValidElement(childContent)) {
          derivedLabel = extractLabel(childContent);
        }
      }

      itemsData.set(value, { value, label: derivedLabel });
      return;
    }

    const props = node.props;
    if (props && typeof props === 'object' && 'children' in props) {
      const nestedChildren = props.children;
      if (nestedChildren) {
        if (Array.isArray(nestedChildren)) {
          nestedChildren.forEach(processNode);
        } else if (nestedChildren !== null && nestedChildren !== undefined) {
          processNode(nestedChildren as React.ReactNode);
        }
      }
    }
  };

  const extractLabel = (node: React.ReactElement, visited = new Set<React.ReactElement>()): string | undefined => {
    if (visited.has(node)) return undefined;
    visited.add(node);

    if (node.type === FilterChipSelectItemLabelSlot) {
      const itemLabelProps = node.props as React.ComponentProps<typeof FilterChipSelectItemLabelSlot>;
      if (typeof itemLabelProps.children === 'string') {
        return itemLabelProps.children;
      }
    }

    const nested =
      node.props && typeof node.props === 'object' && node.props !== null && 'children' in node.props ? node.props.children : undefined;

    if (!nested) return undefined;

    if (Array.isArray(nested)) {
      for (const sub of nested) {
        if (isValidElement(sub)) {
          const result = extractLabel(sub, visited);
          if (result) return result;
        }
      }
      return undefined;
    }

    return isValidElement(nested) ? extractLabel(nested, visited) : undefined;
  };

  const flattenChildren = (children: React.ReactNode): React.ReactNode[] => {
    const result: React.ReactNode[] = [];

    if (Array.isArray(children)) {
      children.forEach((child) => {
        if (Array.isArray(child)) {
          result.push(...flattenChildren(child));
        } else {
          result.push(child);
        }
      });
    } else {
      result.push(children);
    }

    return result;
  };

  const flattenedChildren = flattenChildren(children);

  flattenedChildren.forEach((child) => {
    processNode(child);
  });

  return itemsData;
};
